import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';

export interface MarketplaceAvailabilityResult {
  /** True if at least one verified + listed attorney exists for the given jurisdiction. */
  hasAttorneys: boolean;
  loading: boolean;
}

/**
 * Checks Firestore for at least one verified, listed attorney for a jurisdiction.
 *
 * The check fires automatically whenever an attorney sets
 * `verification.isVerified = true` and `marketplace.isListed = true`
 * in the `professionals` collection, flipping `hasAttorneys` to true
 * for their covered jurisdictions.
 *
 * @param jurisdictionKey - Optional. A jurisdiction key from a case doc, e.g. "US-OR" or
 *   "US-OR-Multnomah". When provided the check is narrowed to attorneys who list that
 *   state (first two segments). When omitted, checks for any available attorney globally.
 */
export function useMarketplaceAvailability(
  jurisdictionKey?: string | null
): MarketplaceAvailabilityResult {
  const [hasAttorneys, setHasAttorneys] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const check = async () => {
      try {
        // Normalize to state-level key: "US-OR-Multnomah" → "US-OR"
        const parts = jurisdictionKey ? jurisdictionKey.split('-') : [];
        const stateKey =
          parts.length >= 2 ? `${parts[0]}-${parts[1]}` : jurisdictionKey ?? null;

        // Query only the deployed 2-field index, then filter test pros AND
        // jurisdiction in code (same approach as MarketplacePage's browse query).
        // We deliberately avoid where('isTestAccount','!=',true): a Firestore "!="
        // needs an undeployed composite index and excludes docs missing the field —
        // and real professional docs never set isTestAccount, so it would hide every
        // real attorney. The verified+listed result set is small, so scanning it here
        // is cheap and index-free.
        const q = query(
          collection(db, 'professionals'),
          where('verification.isVerified', '==', true),
          where('marketplace.isListed', '==', true)
        );

        const snap = await getDocs(q);
        const hasReal = snap.docs.some((d) => {
          const data = d.data();
          if (data.isTestAccount === true) return false; // exclude seeded test pros
          if (!stateKey) return true; // global check — any real attorney counts
          const juris =
            data.marketplace?.jurisdictions ?? data.profile?.jurisdictions ?? [];
          return Array.isArray(juris) && juris.includes(stateKey);
        });
        if (!cancelled) {
          setHasAttorneys(hasReal);
        }
      } catch (err) {
        console.warn('[useMarketplaceAvailability] Check failed:', err);
        if (!cancelled) setHasAttorneys(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [jurisdictionKey]);

  return { hasAttorneys, loading };
}
